// Fuente única de verdad para el gating de MODOS por plan. Antes esto estaba
// disperso en `plan === 'gratis'` sueltos por InterfazV2; centralizarlo acá
// hace fácil auditar/cambiar qué modo necesita qué plan.
//
// Planes (company_profiles.plan): gratis · emprendimiento (Básico) ·
// negocio (Pro) · enterprise/enterprise1/2/3 (Ultra) · y los planes de equipo:
// equipos · agencias1..agencias8 (un id por escalón de créditos) · enterprise_plus
// (el plan a medida, que es Corporativo) — ADR-2026-10-27-planes-de-equipo-en-el-front.

/**
 * Los planes de equipo, a mano. Cada lista de planes de la app que deja pasar a Pro y
 * Ultra los tiene que tener: `planesDeEquipo.test.ts` falla si a alguna le falta uno.
 */
export const PLANES_DE_EQUIPO = [
  'equipos',
  'agencias1',
  'agencias2',
  'agencias3',
  'agencias4',
  'agencias5',
  'agencias6',
  'agencias7',
  'agencias8',
  'enterprise_plus',
] as const;

const PRO_OR_ULTRA = new Set<string>([
  'negocio',
  'enterprise',
  'enterprise1',
  'enterprise2',
  'enterprise3',
  ...PLANES_DE_EQUIPO,
]);

// Ultra y lo que está por encima: los planes de equipo tienen todo lo de Ultra.
const ULTRA = new Set<string>([
  'enterprise',
  'enterprise1',
  'enterprise2',
  'enterprise3',
  ...PLANES_DE_EQUIPO,
]);

const norm = (plan?: string | null) => (plan ?? '').toString().trim().toLowerCase();

/** Pro y Ultra (negocio + enterprise*), y los planes de equipo. */
export function isProOrUltra(plan?: string | null): boolean {
  return PRO_OR_ULTRA.has(norm(plan)) || esPlanDeEquipo(plan);
}

/** Ultra (enterprise/enterprise1/2/3) y los planes de equipo. Excluye Pro (negocio). */
export function isUltra(plan?: string | null): boolean {
  return ULTRA.has(norm(plan)) || esPlanDeEquipo(plan);
}

/** Cualquier plan pago (distinto de gratis). */
export function isPaidPlan(plan?: string | null): boolean {
  const p = norm(plan);
  return p.length > 0 && p !== 'gratis';
}

/** ¿Es un plan de equipo (Equipos, Agencias o Corporativo)? */
export function esPlanDeEquipo(plan?: string | null): boolean {
  // Agencias también por patrón, como el backend y el trigger de la base: los tres tienen que
  // decir lo mismo de un plan. Las listas de permisos de las pantallas sí van a mano.
  const p = norm(plan);
  return (PLANES_DE_EQUIPO as readonly string[]).includes(p) || /^agencias\d+$/.test(p);
}

/**
 * ¿Es un gratis que recién entra? Plan gratis y NUNCA pagó: un ex cliente que volvió a
 * gratis no cuenta. Con esto el teléfono le arma la primera experiencia
 * (ADR-2026-10-23-la-home-del-telefono-para-el-gratis-que-recien-entra).
 */
export function esGratisQueRecienEntra(plan: string | null | undefined, yaPago: boolean): boolean {
  return !isPaidPlan(plan) && !yaPago;
}

/**
 * ¿El plan incluye Equipo (invitar gente a la cuenta)? SÓLO los planes de equipo
 * (ADR-2026-10-27-planes-de-equipo-en-el-front, supersede ADR-2026-09-12-equipo-solo-ultra).
 * Ultra dejó de invitar: los equipos que ya tiene se quedan, pero no suman gente. El backend
 * decide igual en `atlas-backend/src/services/equipo/logic.ts` (`planIncluyeEquipo`): si
 * cambia acá, cambia allá.
 */
export function puedeInvitarEquipo(plan?: string | null): boolean {
  return esPlanDeEquipo(plan);
}

/**
 * Personas en total, dueño incluido, o `null` si no hay tope: Equipos 3, Agencias 50,
 * Corporativo sin límite. Un plan sin equipo, 1 (sólo el dueño). Es `topeDeEquipo` del
 * backend, que es el que lo hace cumplir.
 */
export function topeDeUsuarios(plan?: string | null): number | null {
  const p = norm(plan);
  if (!esPlanDeEquipo(p)) return 1;
  if (p === 'equipos') return 3;
  if (p.startsWith('agencias')) return 50;
  return null;
}

/**
 * Cuántas marcas permite el plan, o `null` si no hay tope: Básico y Pro 1, Ultra 3,
 * Equipos 10, Agencias y Corporativo sin límite. Gratis y lo desconocido, 1. Quien ya tiene
 * más no pierde ninguna: sólo no suma. La regla de verdad es el trigger de `brand_profiles`
 * (`tope_de_marcas_del_plan`, migración 20261027120000); `topeDeMarcas.test.ts` compara las
 * dos tablas.
 */
export function topeDeMarcas(plan?: string | null): number | null {
  const p = norm(plan);
  if (p === 'equipos') return 10;
  if (esPlanDeEquipo(p)) return null;
  if (['enterprise', 'enterprise1', 'enterprise2', 'enterprise3'].includes(p)) return 3;
  return 1;
}

export interface ModePlanLock {
  /** Identificador de la feature bloqueada (para telemetría / debugging). */
  feature:
    // Acá hubo un `'catalogo'` que cubría los dos catálogos e Infografía, y después un
    // `'infografia'` cuando los catálogos se abrieron. Los dos salieron: ese `if` ya no
    // existe — ADR-2026-08-20-infografia-tambien-se-abre-a-la-prueba-gratis.
    //
    // Con eso NINGUNA feature de este union pide "plan pago" a secas: las que quedan son
    // todas de Pro/Ultra para arriba. `prompt-libre` estuvo unas horas con ese piso
    // (ADR-2026-09-12-prompt-libre-y-el-chat-salen-de-interno) y volvió a no tenerlo
    // (ADR-2026-09-12-prompt-libre-entra-en-la-prueba-gratis). `isPaidPlan` se sigue
    // usando afuera: gestionPlan, EnterpriseCheckout, las "Creaciones recientes" de la
    // home y el corte de las funciones avanzadas.
    | 'asistente'
    | 'editor-video'
    | 'video-influencer'
    | 'animar-imagen'
    | 'mostrar-como-funciona-belleza'
    // Creador de video: el piso de video, al modo entero
    // (ADR-2026-09-07-modo-creador-video).
    | 'creador-video'
    // El Agente. NO es un modo de `MODES` —no pasa por `getModePlanLock`— pero comparte
    // este union porque comparte los carteles: la card del sheet de Generar en mobile
    // usa el mismo visor de ejemplos y la misma nota de plan que cualquier modo
    // bloqueado (ADR-2026-09-14-el-agente-es-de-pro-y-ultra).
    | 'agente'
    // Flujos (el canvas de nodos). Tampoco es un modo de `MODES` —es una herramienta de
    // `HERRAMIENTAS_EXTRA`, como Estudio de Video— pero la sidebar le pregunta a
    // `getModePlanLock` por su `id` y así comparte candado y carteles
    // (ADR-2026-09-14-flujos-es-de-pro-y-ultra).
    | 'flujos'
    // Diseño de indumentaria: sólo Moda (por rubro, en el catálogo) y de Pro para arriba
    // (ADR-2026-09-18-el-modo-diseno-se-abre-a-moda-desde-pro).
    | 'diseno-indumentaria'
    // Videos cortos: modo de video, mismo piso (ADR-2026-10-31-videos-cortos-es-de-pro-y-ultra).
    | 'videos-cortos';
  title: string;
  message: string;
}

/** Nota corta del plan que requiere una feature bloqueada — la muestra el
 *  visor de ejemplos de modos (ModeExamplesModal) junto al CTA "Mejorar
 *  plan". Espeja las reglas de getModePlanLock. */
export function getPlanRequirementNote(
  feature: ModePlanLock['feature'],
): string {
  switch (feature) {
    // La rama "desde el plan Básico" se fue con el `if` de catálogo/infografía
    // (ADR-2026-08-20-infografia-tambien-se-abre-a-la-prueba-gratis): hoy no queda
    // ninguna feature con ese piso.
    case 'asistente':
    case 'video-influencer':
    case 'animar-imagen':
    case 'mostrar-como-funciona-belleza':
    case 'creador-video':
    case 'agente':
    case 'flujos':
    case 'diseno-indumentaria':
    case 'videos-cortos':
    // El Estudio de Video pedía Ultra; bajó a Pro para que el agente pueda llevar ahí a
    // quien pide textos o subtítulos (ADR-2026-10-11-el-estudio-de-video-es-de-pro-y-ultra).
    case 'editor-video':
      return 'Función disponible para los planes Pro y Ultra.';
  }
}

/**
 * Devuelve el lock a mostrar si el `plan` NO alcanza para el modo `kind`, o
 * `null` si está permitido.
 *
 * Reglas:
 *   • catalogo-ia / catalogo-muebles / infografia → SIN piso de plan
 *     (ADR-2026-08-20-el-catalogo-se-abre-a-la-prueba-gratis + ...-infografia-tambien-...).
 *   • asistente        → SIN piso de plan; el candado bajó a sus dos opciones caras.
 *   • asistente-v2     → requiere Pro o Ultra: su único camino es la Ayuda de IA.
 *   • editor-video     → requiere Pro o Ultra (era Ultra hasta
 *     ADR-2026-10-11-el-estudio-de-video-es-de-pro-y-ultra).
 *   • video-influencer → requiere Pro o Ultra (ADR-564).
 *   • animar-imagen    → requiere Pro o Ultra.
 *   • mostrar-como-funciona-belleza → ídem: sale video por el MISMO pipeline.
 *   • creador-video    → requiere Pro o Ultra (es sólo video).
 *   • prompt-libre     → SIN piso de plan, como los modos de imagen abiertos
 *     (ADR-2026-09-12-prompt-libre-entra-en-la-prueba-gratis).
 *   • flujos           → requiere Pro o Ultra (ADR-2026-09-14-flujos-es-de-pro-y-ultra).
 *   • diseno-indumentaria → requiere Pro o Ultra; el rubro (sólo Moda) lo filtra el
 *     catálogo (ADR-2026-09-18-el-modo-diseno-se-abre-a-moda-desde-pro).
 *
 * IMPORTANTE: si `plan` todavía no se conoce (null/''), NO bloqueamos — así no
 * le negamos el acceso a un usuario pago mientras su plan carga. El guard de
 * generación (que pide el plan fresco a Supabase) queda como backstop.
 */
export function getModePlanLock(
  kind: string | null | undefined,
  plan: string | null | undefined,
): ModePlanLock | null {
  if (!kind || !norm(plan)) return null;

  // LOS TRES MODOS DE LA FAMILIA CATÁLOGO YA NO SE BLOQUEAN: `catalogo-ia`,
  // `catalogo-muebles` e `infografia`.
  //
  // Acá vivía un `if` con `isPaidPlan` para los tres. Se desarmó en dos pasos:
  // ADR-2026-08-20-el-catalogo-se-abre-a-la-prueba-gratis sacó los dos catálogos y dejó
  // Infografía con `if` propio; ADR-2026-08-20-infografia-tambien-se-abre-a-la-prueba-gratis
  // (que lo supersede) sacó también Infografía. Quedan sin piso de plan los tres.
  //
  // Lo único que los limita en la prueba gratis son los CRÉDITOS, y ese corte ya
  // existía: `checkGenerationGuards('catalogo', cost)` chequea saldo SUFICIENTE contra
  // el costo real de la tanda —1 crédito por imagen = vista × producto, ADR-518—, así
  // que una tanda más cara que el saldo no pasa igual.
  //
  // NO dejar un `if` vacío ni un comentario "acá iría el gate": el orden de los `if` de
  // esta función es su semántica, y una rama muerta se lee como una regla viva.

  // "Crear desde cero" (`asistente`) YA NO SE BLOQUEA —
  // ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.
  //
  // Acá vivía `kind === 'asistente' && !isProOrUltra(plan)`. El modo pasó a estar
  // abierto para todos los planes, y lo que quedó gateado son sus dos opciones caras:
  // la ayuda de IA y la salida en video. Ver `puedeUsarAyudaIA` y
  // `puedeGenerarVideoEnAsistente` más abajo.
  //
  // La `feature: 'asistente'` NO se borra del union: la siguen usando
  // `getPlanRequirementNote` y los dos modales de las opciones.

  // `asistente-v2` SÍ SE BLOQUEA, y es la excepción a la línea de arriba —
  // ADR-2026-09-08-asistente-v2-es-una-variante-del-mismo-modo.
  //
  // v1 se abrió a todos los planes porque tiene DOS caminos y el gratuito (el directo,
  // sin LLM) alcanza para usar el modo entero. v2 sacó el desplegable de Ayuda de IA:
  // la ayuda queda prendida por detrás y no hay camino directo, así que el modo ES la
  // opción Pro. Sin este `if`, entrar a v2 con un plan que no llega sería el upgrade
  // silencioso que `puedeUsarAyudaIA` justamente evita — y además una pantalla rota: el
  // backstop de `handleGenerate` cortaría cada envío contra un candado que no se puede
  // apagar desde ninguna parte.
  if (kind === 'asistente-v2' && !isProOrUltra(plan)) {
    return {
      feature: 'asistente',
      title: 'El asistente es una función Pro',
      message:
        'Que Atlas converse, elija y arme el pedido por vos está disponible para los planes Pro y Ultra. Podés seguir usando "Crear desde cero" escribiendo vos el pedido.',
    };
  }

  if (kind === 'video-influencer' && !isProOrUltra(plan)) {
    return {
      feature: 'video-influencer',
      title: 'Video con influencer es una función Pro',
      message:
        'La generación de videos con influencer IA está disponible para los planes Pro y Ultra. Mejorá tu plan para usarla.',
    };
  }

  // Creador de video: el segundo modo de la partición del asistente
  // (ADR-2026-09-07-modo-creador-video). Es SÓLO video, así que el piso que el
  // asistente le pone a su opción de video (`puedeGenerarVideoEnAsistente`, Pro/Ultra)
  // acá es el piso del modo entero — sin video no queda nada que hacer adentro.
  if (kind === 'creador-video' && !isProOrUltra(plan)) {
    return {
      feature: 'creador-video',
      title: 'Creador de video es una función Pro',
      message:
        'Generar videos desde un prompt está disponible para los planes Pro y Ultra. Mejorá tu plan para usarlo.',
    };
  }

  // PROMPT LIBRE NO TIENE PISO DE PLAN — ADR-2026-09-12-prompt-libre-entra-en-la-prueba-gratis,
  // que supersede el piso de plan pago que le había puesto
  // ADR-2026-09-12-prompt-libre-y-el-chat-salen-de-interno (vivió unas horas).
  //
  // Genera imágenes a 1 crédito, igual que Automática o Modelo, que están abiertas: lo que
  // lo limita en la prueba gratis son los CRÉDITOS, y ese corte ya existe. Acá no va
  // ningún `if`: una rama vacía se lee como una regla viva.

  // Animar imagen consume SEGUNDOS de video: mismo piso de plan que el resto de
  // los modos de video. El botón viejo del historial ya pedía Pro/Ultra, así que
  // esto no restringe nada nuevo — sólo lo mueve a la entrada del modo.
  if (kind === 'animar-imagen' && !isProOrUltra(plan)) {
    return {
      feature: 'animar-imagen',
      title: 'Animar imagen es una función Pro',
      message:
        'Convertir tus imágenes en video está disponible para los planes Pro y Ultra. Mejorá tu plan para usarla.',
    };
  }

  // Recrear video es para todos los planes: cobra 3 créditos por segundo y el saldo es el
  // único límite (ADR-2026-10-16-recrear-video-es-para-todos-los-planes).

  // Videos cortos: un video por producto, 12 créditos cada uno. Es de video, así que lleva el
  // piso del resto de los modos de video (ADR-2026-10-31-videos-cortos-es-de-pro-y-ultra). El
  // backend lo vuelve a mirar al lanzar el lote.
  if (kind === 'videos-cortos' && !isProOrUltra(plan)) {
    return {
      feature: 'videos-cortos',
      title: 'Videos cortos es una función Pro',
      message:
        'Crear videos cortos de tus productos está disponible para los planes Pro y Ultra. Mejorá tu plan para usarlo.',
    };
  }

  // "Mostrar cómo funciona" (Belleza y Cuidado Personal) sale por el MISMO endpoint que
  // Animar imagen (`/v1/videos/from-image`) y cobra los mismos segundos, así que le
  // corresponde el mismo piso de plan — ADR-2026-09-10-mostrar-como-funciona-belleza-corre-sobre-animar-imagen.
  // Sin esta entrada el modo se listaba sin candado y el corte llegaba recién en el
  // guard del Generar, o sea después de haber configurado todo.
  if (kind === 'mostrar-como-funciona-belleza' && !isProOrUltra(plan)) {
    return {
      feature: 'mostrar-como-funciona-belleza',
      title: 'Mostrar cómo funciona es una función Pro',
      message:
        'Convertir tus fotos en video está disponible para los planes Pro y Ultra. Mejorá tu plan para usarla.',
    };
  }

  // El Estudio de Video: Pro y Ultra. Pedía Ultra; bajó a Pro porque es donde van los
  // textos, los subtítulos y los retoques de un video, y el agente lleva ahí a quien los
  // pide (ADR-2026-10-11-el-estudio-de-video-es-de-pro-y-ultra).
  if (kind === 'editor-video' && !isProOrUltra(plan)) {
    return {
      feature: 'editor-video',
      title: 'El Estudio de Video es una función Pro',
      message:
        'El Estudio de Video con IA está disponible para los planes Pro y Ultra. Mejorá tu plan para desbloquearlo.',
    };
  }

  // Flujos: el canvas de nodos encadena imagen → voz → video y vuelve a correr la
  // cadena con otro producto; adentro hay generación de video, que ya es Pro/Ultra en
  // todos los modos, así que el piso de la herramienta entera es ése
  // (ADR-2026-09-14-flujos-es-de-pro-y-ultra). Antes no tenía piso: era `dev_tester`
  // solamente.
  //
  // `kind` acá es el `id` de la herramienta ('flujos'), que es lo que la sidebar manda
  // cuando `MODE_PATH_TO_KIND` no lo conoce — igual que 'editor-video'.
  if (kind === 'diseno-indumentaria' && !isProOrUltra(plan)) {
    return {
      feature: 'diseno-indumentaria',
      title: 'Diseño de indumentaria es una función Pro',
      message:
        'Diseñar prendas en el lienzo —mostrarlas en uso, mezclar texturas e inspiraciones y sacar el plano técnico— está disponible para los planes Pro y Ultra. Mejorá tu plan para usarlo.',
    };
  }

  if (kind === 'flujos' && !puedeUsarFlujos(plan)) {
    return {
      feature: 'flujos',
      title: 'Flujos es una función Pro',
      message:
        'Encadenar nodos —producto, prompt, imagen, voz, video— y volver a correrlos con otro producto está disponible para los planes Pro y Ultra. Mejorá tu plan para usarlo.',
    };
  }

  return null;
}

/**
 * ¿Este plan alcanza para Flujos? Pro y Ultra (Empresa incluido) —
 * ADR-2026-09-14-flujos-es-de-pro-y-ultra.
 *
 * Mismo par que el Agente: ésta es la pregunta de quien DIBUJA una puerta o decide con
 * el plan ya en la mano (el plan desconocido NO alcanza); `getModePlanLock('flujos')`
 * es la de quien cierra una ya abierta (con el plan desconocido no bloquea). Las
 * pantallas de Flujos usan ésta a través de `flujosVisible`, porque llegan con el plan
 * autoritativo de `fetchCuentaFlujos` y ahí "no sé" no existe.
 */
export function puedeUsarFlujos(plan?: string | null): boolean {
  return isProOrUltra(plan);
}

/* ── "Crear desde cero": el modo se abre, las opciones caras no ──────────────
   ADR-2026-08-20-crear-desde-cero-se-abre-y-el-candado-baja-a-las-opciones.

   El modo entero pedía Pro/Ultra. Ahora entra cualquiera —incluida la prueba
   gratis— y el piso de plan se movió a las dos opciones que cuestan de verdad.

   Las DOS piden `isProOrUltra` y no `isPaidPlan`: es exactamente el piso que el modo
   tenía antes, así que a Básico no le cambia nada. Con `isPaidPlan` habrían quedado
   desbloqueadas para Básico, que es un plan al que hoy no le corresponden — un upgrade
   silencioso colado en un cambio que era de otra cosa.

   OJO: son helpers de UI. El backend NO los conoce, y el camino de generación del
   asistente no consulta el plan (`checkGenerationGuards('agente')` existe pero ningún
   call site lo usa). Por eso además del candado visual hay un corte en el submit. */

/** ¿Puede prender la "ayuda de IA" del asistente? Pro y Ultra. */
export function puedeUsarAyudaIA(plan?: string | null): boolean {
  return isProOrUltra(plan);
}

/** ¿Puede pedir la salida en VIDEO desde el asistente? Pro y Ultra. */
export function puedeGenerarVideoEnAsistente(plan?: string | null): boolean {
  return isProOrUltra(plan);
}

/**
 * Igual que `getModePlanLock` pero para las dos opciones de arriba: devuelve el lock a
 * mostrar, o `null` si está permitida.
 *
 * Mismo criterio de plan desconocido que `getModePlanLock`: con `plan` en null/'' NO se
 * bloquea, así no se le niega la opción a alguien pago mientras su plan carga.
 */
export function getOpcionAsistenteLock(
  opcion: 'ayuda-ia' | 'video',
  plan: string | null | undefined,
): ModePlanLock | null {
  if (!norm(plan)) return null;
  if (opcion === 'ayuda-ia') {
    if (puedeUsarAyudaIA(plan)) return null;
    return {
      feature: 'asistente',
      title: 'La ayuda de IA es una función Pro',
      message:
        'Que Atlas te ayude a armar el pedido está disponible para los planes Pro y Ultra. Podés seguir usando "Crear desde cero" escribiendo vos el pedido.',
    };
  }
  if (puedeGenerarVideoEnAsistente(plan)) return null;
  return {
    feature: 'asistente',
    title: 'Generar video es una función Pro',
    message:
      'La salida en video está disponible para los planes Pro y Ultra. Podés seguir generando imágenes con "Crear desde cero".',
  };
}

/* ── El Agente: para todos; Media/Alta y video, Pro y Ultra ─────────────────
   ADR-2026-10-12-el-agente-es-de-todos (supersede ADR-2026-09-14-el-agente-es-de-pro-y-ultra).
   El backend aplica lo mismo: la inteligencia Media/Alta y los videos del Agente
   rechazan a Gratis y Básico aunque se llame a la API directo. */

/**
 * ¿Este plan alcanza para el Agente? Todos, la prueba gratis incluida
 * (ADR-2026-10-12-el-agente-es-de-todos; supersede ADR-2026-09-14-el-agente-es-de-pro-y-ultra).
 * Lo que sigue siendo de Pro y Ultra es la inteligencia Media y Alta (`puedeSubirInteligencia`)
 * y generar videos (`puedeGenerarVideosEnElAgente`).
 *
 * Se mantiene la función (y `getAgenteLock` y `decidirAccesoAlAgente`) porque la leen todas
 * las puertas del Agente: si un día vuelve a tener piso, se cambia acá.
 */
export function puedeUsarElAgente(_plan?: string | null): boolean {
  return true;
}

/** Qué puede hacer el guard de la pantalla del Agente. Con el Agente para todos, siempre pasa. */
export type AccesoAlAgente = 'cargando' | 'permitido' | 'denegado';

export function decidirAccesoAlAgente(_p: {
  plan: string | null | undefined;
  isDevTester: boolean | null;
  plazoVencido: boolean;
}): AccesoAlAgente {
  return 'permitido';
}

/** El lock del Agente: ninguno, es para todos los planes. */
export function getAgenteLock(_plan: string | null | undefined): ModePlanLock | null {
  return null;
}

/**
 * ¿Puede generar videos desde el Agente? Pro y Ultra, como los modos de video. El plan
 * desconocido no bloquea (mismo criterio que `getModePlanLock`): el backend tiene la
 * última palabra.
 */
export function puedeGenerarVideosEnElAgente(plan?: string | null): boolean {
  return !norm(plan) || isProOrUltra(plan);
}
